import { z } from 'zod'

/**
 * CRM shared types: workstream F (API keys, outbound calls and webhooks, calendars).
 * Design 7.3 "an API and outbound webhooks for every object", 7.6 "working with other systems".
 */

// ---------------------------------------------------------------------------
// API keys and service accounts
// ---------------------------------------------------------------------------
/**
 * Scopes an API key can hold. `crm:read`, `crm:write` and `crm:delete` cover every object; a scope ending in
 * `:<object>` covers one object (`person`, `organisation` or a custom object key, e.g. `crm:write:booking`).
 * `pii:read` lets the key see fields marked as personal data that its service account's role may see.
 */
export const API_SCOPES: { scope: string; label: string; detail: string }[] = [
  { scope: 'crm:read', label: 'Read CRM records', detail: 'People, organisations, custom records, their timelines and the object list.' },
  { scope: 'crm:write', label: 'Create and change CRM records', detail: 'Also allows reading. Includes adding notes.' },
  { scope: 'crm:delete', label: 'Delete CRM records', detail: 'Custom records only; people are erased from the console.' },
  { scope: 'pii:read', label: 'See personal data fields', detail: 'Fields marked as personal data, when the role allows them.' },
]
const SCOPE = /^(\*|pii:read|crm:\*|crm:(read|write|delete)(:[a-z][a-z0-9_]{0,62})?)$/
export const ApiScope = z.string().regex(SCOPE, 'crm:read, crm:write, crm:delete (optionally :<object>), pii:read')

export const ServiceAccountCreate = z.object({
  name: z.string().trim().min(2).max(80),
  description: z.string().trim().max(300).nullish(),
  role: z.enum(['specialist', 'supervisor']).default('specialist'),
  scopes: z.array(ApiScope).min(1).max(40),
  ip_allow: z.array(z.string().trim().min(3).max(64)).max(20).default([]),
  rate_per_minute: z.number().int().min(1).max(10_000).default(120),
  expires_in_days: z.number().int().min(1).max(3650).nullish(),
})
export const ApiKeyRotate = z.object({ grace_hours: z.number().int().min(0).max(168).default(24) })
export const ApiKeyPatch = z.object({
  scopes: z.array(ApiScope).min(1).max(40).optional(),
  ip_allow: z.array(z.string().trim().min(3).max(64)).max(20).optional(),
  rate_per_minute: z.number().int().min(1).max(10_000).optional(),
})

export interface ApiKeyView {
  id: string; prefix: string; scopes: string[]; ip_allow: string[]; rate_per_minute: number
  status: 'active' | 'expiring' | 'expired' | 'revoked'
  expires_at: string | null; revoked_at: string | null; last_used_at: string | null; last_used_ip: string | null
  rotated_from: string | null; rotated_to: string | null; created_at: string
}
export interface ServiceAccountView {
  id: string; name: string; description: string | null; role: 'specialist' | 'supervisor'
  disabled: boolean; created_at: string; keys: ApiKeyView[]
}
/** A key with its secret: returned once, when it is created or rotated. */
export interface ApiKeySecret { key: ApiKeyView; secret: string; service_account_id: string }

// ---------------------------------------------------------------------------
// Events: what webhooks and connectors can listen for
// ---------------------------------------------------------------------------
export interface EventKind { kind: string; group: string; label: string }
export const EVENT_CATALOGUE: EventKind[] = [
  { kind: 'person.created', group: 'People', label: 'A person was added' },
  { kind: 'person.updated', group: 'People', label: 'A person\'s details changed' },
  { kind: 'person.merged', group: 'People', label: 'Two people were merged' },
  { kind: 'person.unmerged', group: 'People', label: 'A merge was undone' },
  { kind: 'organisation.created', group: 'Organisations', label: 'An organisation was added' },
  { kind: 'organisation.updated', group: 'Organisations', label: 'An organisation changed' },
  { kind: 'record.created', group: 'Records', label: 'A custom record was created (bookings, orders…)' },
  { kind: 'record.updated', group: 'Records', label: 'A custom record changed' },
  { kind: 'record.deleted', group: 'Records', label: 'A custom record was deleted' },
  { kind: 'note.created', group: 'Records', label: 'A note was added' },
  { kind: 'conversation.opened', group: 'Conversations', label: 'A conversation started' },
  { kind: 'conversation.resolved', group: 'Conversations', label: 'A conversation was resolved' },
  { kind: 'message.received', group: 'Conversations', label: 'A customer message arrived' },
  { kind: 'message.sent', group: 'Conversations', label: 'A message was sent to a customer' },
  { kind: 'message.failed', group: 'Conversations', label: 'A message could not be sent' },
  { kind: 'call.ended', group: 'Conversations', label: 'A call ended' },
  { kind: 'task.created', group: 'Tasks', label: 'A task was created' },
  { kind: 'task.done', group: 'Tasks', label: 'A task was finished' },
  { kind: 'invoice.created', group: 'Money', label: 'An invoice was created' },
  { kind: 'invoice.issued', group: 'Money', label: 'An invoice was issued' },
  { kind: 'invoice.paid', group: 'Money', label: 'An invoice was paid' },
  { kind: 'invoice.voided', group: 'Money', label: 'An invoice was voided' },
  { kind: 'payment.succeeded', group: 'Money', label: 'A payment succeeded' },
  { kind: 'payment.failed', group: 'Money', label: 'A payment failed' },
  { kind: 'refund.succeeded', group: 'Money', label: 'A refund went through' },
  { kind: 'refund.failed', group: 'Money', label: 'A refund failed' },
]
/** `*`, a catalogue kind, any kind seen recently, or a group wildcard such as `record.*`. */
export const EventPattern = z.string().trim().regex(/^(\*|[a-z_]+\.(\*|[a-z_.]+))$/, 'an event kind such as record.created, or record.*')
/** Whether an endpoint's or connector's patterns match an event kind. */
export function eventMatches(patterns: string[], kind: string) {
  return patterns.some((p) => p === '*' || p === kind || (p.endsWith('.*') && kind.startsWith(p.slice(0, -1))))
}

// ---------------------------------------------------------------------------
// Webhooks: delivery log
// ---------------------------------------------------------------------------
export const WebhookPatch = z.object({ active: z.boolean().optional(), events: z.array(EventPattern).min(1).optional(), description: z.string().max(200).nullish() })
export interface WebhookDeliveryView {
  id: string; event_id: string; event_kind: string; status: 'pending' | 'delivered' | 'failed'; attempts: number
  last_status: number | null; last_error: string | null; response_snippet: string | null
  next_at: string | null; last_attempt_at: string | null; delivered_at: string | null; created_at: string
}
export interface WebhookHealthView {
  id: string; url: string; events: string[]; active: boolean; description: string | null; created_at: string
  consecutive_failures: number; last_success_at: string | null; last_failure_at: string | null; paused_reason: string | null
  pending: number; failed_24h: number
}

// ---------------------------------------------------------------------------
// Connectors: outbound API calls on events (design 7.6)
// ---------------------------------------------------------------------------
export const ConnectorHeader = z.object({
  name: z.string().trim().regex(/^[A-Za-z0-9-]{1,64}$/, 'letters, digits and dashes'),
  /** A plain value, which may use {{placeholders}}. Omit when `secret` is set. */
  value: z.string().max(2000).optional(),
  /** A secret value (an API token): stored encrypted and never shown again. Send null to keep the stored one. */
  secret: z.string().max(4000).nullish(),
})
export const ConnectorRequest = z.object({
  method: z.enum(['POST', 'PUT', 'PATCH', 'DELETE', 'GET']).default('POST'),
  url: z.string().trim().min(8).max(2000),
  headers: z.array(ConnectorHeader).max(20).default([]),
  /** A JSON template. Strings that are exactly "{{path}}" take the value's type; others are text with values in. */
  body: z.unknown().optional(),
})
export const ConnectorTrigger = z.object({
  event: EventPattern,
  /** Only events whose values at these paths equal these values, e.g. {"data.object": "booking"}. */
  filter: z.record(z.string().regex(/^[a-z_][a-z0-9_.]{0,120}$/), z.union([z.string(), z.number(), z.boolean(), z.null()])).default({}),
})
export const ConnectorInput = z.object({
  name: z.string().trim().min(2).max(100),
  trigger: ConnectorTrigger,
  request: ConnectorRequest,
  /** Sign each request with x-takeit4me-signature (HMAC-SHA256), as webhooks are. */
  signing: z.boolean().default(true),
  task_id: z.uuid().nullish(),
})
export type ConnectorInput = z.infer<typeof ConnectorInput>
export interface ConnectorView {
  id: string; name: string; kind: 'http'; status: 'draft' | 'live' | 'paused'; version: number
  trigger: { event: string; filter: Record<string, string | number | boolean | null> }
  request: { method: string; url: string; headers: { name: string; value?: string; secret: boolean }[]; body?: unknown }
  signing: boolean; has_signing_secret: boolean
  tested: boolean; tested_at: string | null; source: 'manual' | 'ai'; prompt: string | null; task_id: string | null
  consecutive_failures: number; last_success_at: string | null; last_failure_at: string | null
  approved_by: string | null; approved_at: string | null; created_at: string; updated_at: string
  runs: { pending: number; failed_24h: number; delivered_24h: number }
}
export interface ConnectorRunView {
  id: string; event_id: string; event_kind: string; status: 'pending' | 'delivered' | 'failed' | 'cancelled'; attempts: number
  last_status: number | null; last_error: string | null; response_snippet: string | null
  request: { method: string; url: string; headers: Record<string, string>; body: string | null } | null
  next_at: string | null; last_attempt_at: string | null; delivered_at: string | null; created_at: string
}
/** A dry run: the request that would be sent for an event, without sending it (unless `send` was asked). */
export interface ConnectorTestResult {
  ok: boolean; problems: string[]; event: { id: string | null; kind: string; sample: boolean }
  request: { method: string; url: string; headers: Record<string, string>; body: string | null } | null
  sent: { status: number; ok: boolean; snippet: string | null } | null
}
export const ConnectorTestInput = z.object({
  /** An event to try it on; otherwise the latest matching event, otherwise a sample. */
  event_id: z.string().regex(/^\d+$/).optional(),
  /** Also send it (to a test endpoint), not only show it. */
  send: z.boolean().default(false),
})
export const ConnectorDraftInput = z.object({ request: z.string().trim().min(10).max(4000), task_id: z.uuid().nullish() })

// ---------------------------------------------------------------------------
// Calendars (design 7.6 "calendars")
// ---------------------------------------------------------------------------
export const CalendarConnect = z.object({
  provider: z.enum(['google', 'microsoft']),
  label: z.string().trim().min(2).max(80),
  staff_id: z.uuid().nullish(),
  /** Google calendar ids or Microsoft mailbox addresses to read; default the account's own calendar. */
  calendars: z.array(z.string().trim().min(1).max(200)).max(20).optional(),
})
export interface CalendarConnectionView {
  id: string; provider: 'google' | 'microsoft'; label: string; staff_id: string | null; account_email: string | null
  calendars: string[]; status: 'pending' | 'connected' | 'error' | 'disconnected'; last_error: string | null
  last_checked_at: string | null; created_at: string
}
export interface CalendarProvidersView { google: boolean; microsoft: boolean; redirect_uri: string }
export interface AvailabilityView {
  from: string; to: string; slot_minutes: number; timezone: string
  busy: { start: string; end: string; connection: string }[]
  free: { start: string; end: string }[]
  /** Calendars that could not be read: the free slots then may be wrong, and staff should check by hand. */
  problems: { connection: string; message: string }[]
}
